import { useState, useRef } from "react";
import styled from "styled-components";
import { isEmpty } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faClose } from "@fortawesome/free-solid-svg-icons";
import { withAppHOC } from "../../hoc";
import {
  StyledContentContainer,
  StyledMainAreaContainer,
} from "../../components/styled";
import PageHeader from "../../components/PageHeader/PageHeader";
import { Image } from "../../components/Image";
import {
  Form,
  Fieldset,
  Label,
  TextInput,
  MobileInput,
  Dropdown,
  MessageBox,
  Button,
  Legend,
  Switch,
} from "../../components/FormElements";
import API from "../../api";
import { getAuthData, getBase64 } from "../../utils";
import { RESPONSE_STATUSES } from "../../constants";
import { useQuery } from "@tanstack/react-query";
import Grid from "../../components/Grid/Grid";
import { GridItem } from "../../components/Grid";

const StyledPhotoContainer = styled.section`
  width: auto;
  height: fit-content;
  @media (max-width: 600px) {
    max-width: 180px;
  }
`;

const StyledPhotoImageContainer = styled.section`
  width: 150px;
  height: 150px;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
  padding: 10px;
  border: 5px solid
    ${({ theme: { colors } }) => colors.primaryColor || `#3049f8`};
  border-bottom: 0px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
`;

const StyledPhotoSelector = styled.section`
  width: auto;
`;

const StyledPhotoButton = styled.button`
  border-width: 0px;
  background-color: ${({ theme: { colors } }) =>
    colors.primaryColor || `#3049f8`};
  padding: 15px 10px;
  width: 100%;
  font-size: 14px;
  font-weight: normal;
  color: #ffffff;
  display: flex;
  flex-direction: row;
  column-gap: 10px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  &:hover {
    background-color: #008a7d;
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const StyledFormContainer = styled.section`
  width: 100%;
  flex: 1;
`;

const StyledInput = styled.input`
  display: none;
`;

const Profile = (props) => {
  const {
    userProfile: { gstInvoiceP2AStatus, gstInvoiceP2PStatus },
  } = getAuthData();
  const gstTypeOptions = [{ name: "-- SELECT GST TYPE --", value: "" }];
  if (gstInvoiceP2PStatus && gstInvoiceP2PStatus === "Active") {
    gstTypeOptions.push({
      name: "P2P",
      value: "P2P",
    });
  }
  if (gstInvoiceP2AStatus && gstInvoiceP2AStatus === "Active") {
    gstTypeOptions.push({
      name: "P2A",
      value: "P2A",
    });
  }
  const inputRef = useRef(null);
  const [state, setState] = useState({
    isLoading: false,
    responseStatus: "",
    responseMessage: "",

    photo: "",
    photoBase64: "",

    fullName: "",
    mobileNumber: "",
    gender: "",
    dateOfBirth: "",

    firmName: "",
    firmType: "",
    gstNumber: "",
    gstName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    turnover: "",

    panNumber: "",
    panName: "",
    aadhaarNumber: "",
    aadhaarName: "",

    autoWalletTransferStatus: "",
    autoWalletTransferAmount: "",

    alertNumber: "",
    alertType: "",
    email: "",
    lowBalAlertStatus: "",
    lowBalAlertAmount: "",

    kycStatus: "",
  });

  const readData = async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: "",
      responseMessage: "",
    }));
    try {
      const response = await API.get("/authentication/profile");
      const { status, message, data } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setState((prevState) => ({
          ...prevState,
          responseStatus: "",
          responseMessage: "",

          photo: data?.photo,

          fullName: data?.fullName,
          mobileNumber: data?.mobileNumber,
          gender: data?.gender,
          dateOfBirth: data?.dateOfBirth,

          firmName: data?.firmName,
          firmType: data?.firmType,
          gstNumber: data?.gstNumber,
          gstName: data?.gstName,
          addressLine1: data?.addressLine1,
          addressLine2: data?.addressLine2,
          city: data?.city,
          state: data?.state,
          pincode: data?.pincode,
          turnover: data?.turnover,

          panNumber: data?.panNumber,
          panName: data?.panName,
          aadhaarNumber: data?.aadhaarNumber,
          aadhaarName: data?.aadhaarName,

          autoWalletTransferStatus: data?.autoWalletTransferStatus,
          autoWalletTransferAmount: data?.autoWalletTransferAmount,

          alertNumber: data?.alertNumber,
          alertType: data?.alertType,
          email: data?.email,
          lowBalAlertStatus: data?.lowBalAlertStatus,
          lowBalAlertAmount: data?.lowBalAlertAmount,

          kycStatus: data?.kycStatus,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
        }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: "",
      responseMessage: "",
    }));
    try {
      const response = await API.put("/authentication/profile", {
        photo:
          state.photoBase64 && !isEmpty(state.photoBase64)
            ? state.photoBase64
            : state.photo,

        fullName: state.fullName,
        mobileNumber: state.mobileNumber,
        gender: state.gender,
        dateOfBirth: state.dateOfBirth,

        firmName: state.firmName,
        firmType: state.firmType,
        gstNumber: state.gstNumber,
        gstName: state.gstName,
        addressLine1: state.addressLine1,
        addressLine2: state.addressLine2,
        city: state.city,
        state: state.state,
        pincode: state.pincode,
        turnover: state.turnover,

        panNumber: state.autoWalletTransferStatus,
        panName: state.autoWalletTransferStatus,
        aadhaarNumber: state.autoWalletTransferStatus,
        aadhaarName: state.autoWalletTransferStatus,

        autoWalletTransferStatus: state.autoWalletTransferStatus,
        autoWalletTransferAmount: state.autoWalletTransferAmount,

        alertNumber: state.alertNumber,
        alertType: state.alertType,
        email: state.email,
        lowBalAlertStatus: state.lowBalAlertStatus,
        lowBalAlertAmount: state.lowBalAlertAmount,
      });
      const { status, message } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        // setLocalStorage("userProfile", JSON.stringify(data));
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.SUCCESS,
          responseMessage: message,
        }));
        setTimeout(() => {
          setState((prevState) => ({
            ...prevState,
            responseStatus: "",
            responseMessage: "",
          }));
          // if (shouldReload) {
          //   clearLocalStorage();
          //   window.location.href = "/login";
          // }
        }, 3000);
      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
        }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const openFileSelector = () => {
    inputRef.current.click();
  };

  useState(() => {
    readData();
  }, []);

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: metaData } = useQuery({
    queryKey: ["meta_data"],
    queryFn: readMeta,
  });

  const firmTypeOptions = [{ name: "-- SELECT FIRM TYPE --", value: "" }];

  if (metaData && metaData?.firmTypes && metaData?.firmTypes?.length > 0) {
    metaData?.firmTypes?.forEach(({ id, type }) => {
      firmTypeOptions.push({
        name: type,
        value: id,
      });
    });
  }

  return (
    <>
      <PageHeader title="Your Profile" />
      <StyledContentContainer>
        <StyledMainAreaContainer>
          <StyledPhotoContainer>
            <StyledPhotoImageContainer>
              <Image
                source={
                  state.photoBase64
                    ? state.photoBase64
                    : state.photo
                    ? state.photo
                    : "default-profile-photo.png"
                }
                alt={"Seelam Sivasankar Naidu"}
              />
            </StyledPhotoImageContainer>
            <StyledPhotoSelector>
              <StyledInput
                type="file"
                ref={inputRef}
                onChange={(e) => {
                  getBase64(e.target.files[0], (photoBase64) => {
                    setState((prevState) => ({
                      ...prevState,
                      photoBase64: photoBase64,
                    }));
                  });
                }}
                accept="image/*"
                disabled={state.isLoading}
              />
              <StyledPhotoButton
                disabled={state.isLoading}
                onClick={() => {
                  if (!state.isLoading) {
                    if (!isEmpty(state.photoBase64) || !isEmpty(state.photo)) {
                      inputRef.current.value = null;
                      setState((prevState) => ({
                        ...prevState,
                        photoBase64: "",
                        photo: "",
                      }));
                    } else {
                      openFileSelector();
                    }
                  }
                }}
              >
                {!isEmpty(state.photoBase64) || !isEmpty(state.photo) ? (
                  <>
                    <FontAwesomeIcon icon={faClose} /> Remove Photo
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCamera} /> Select Photo
                  </>
                )}
              </StyledPhotoButton>
            </StyledPhotoSelector>
          </StyledPhotoContainer>
          <StyledFormContainer>
            <Form action="#" method="POST" onSubmit={onSubmit}>
              <Fieldset forlegend display="block">
                <Legend>Basic Info</Legend>
                <Grid>
                  <GridItem>
                    <Fieldset>
                      <Label required>Full Name</Label>
                      <TextInput
                        value={state.fullName}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            fullName: value,
                          }))
                        }
                        placeholder="Full Name"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>Mobile Number</Label>
                      <MobileInput
                        value={state.mobileNumber}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            mobileNumber: value,
                          }))
                        }
                        placeholder="Mobile Number"
                        disabled={true}
                      />
                    </Fieldset>
                  </GridItem>
                  {/* <GridItem>
                    <Fieldset>
                      <Label required>Gender</Label>
                      <Dropdown
                        value={state.gender}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            gender: value,
                          }))
                        }
                        placeholder="Gender"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                        options={[
                          {
                            name: "-- SELECT GENDER --",
                            value: "",
                          },
                          {
                            name: "Male",
                            value: "Male",
                          },
                          {
                            name: "Female",
                            value: "Female",
                          },
                          {
                            name: "Others",
                            value: "Others",
                          },
                        ]}
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>Date Of Birth</Label>
                      <TextInput
                        type="date"
                        value={state.dateOfBirth}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            dateOfBirth: value,
                          }))
                        }
                        placeholder="Date Of Birth"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem> */}
                </Grid>
              </Fieldset>

              <Fieldset forlegend display="block">
                <Legend>Business Details</Legend>
                <Grid>
                  <GridItem>
                    <Fieldset>
                      <Label required>Firm Name</Label>
                      <TextInput
                        value={state.firmName}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            firmName: value,
                          }))
                        }
                        placeholder="Firm Name"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>Firm Type</Label>
                      <Dropdown
                        value={state.firmType}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            firmName: value,
                          }))
                        }
                        placeholder="Firm Type"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                        options={firmTypeOptions}
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>GST Number</Label>
                      <TextInput
                        value={state.gstNumber}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            gstNumber: value,
                            gstName: "",
                            gstFile: "",
                          }))
                        }
                        placeholder="GST Number"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>Name on GST</Label>
                      <TextInput
                        value={state.gstName}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            gstName: value,
                          }))
                        }
                        placeholder="Name on GST"
                        disabled={true}
                      />
                    </Fieldset>
                  </GridItem>
                </Grid>
                <Grid>
                  <GridItem>
                    <Fieldset>
                      <Label required>Address Line 1</Label>
                      <TextInput
                        value={state.addressLine1}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            addressLine1: value,
                          }))
                        }
                        placeholder="Address Line 1"
                        maxLength={1000}
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>Address Line 2</Label>
                      <TextInput
                        value={state.addressLine2}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            addressLine2: value,
                          }))
                        }
                        placeholder="Address Line 2"
                        maxLength={1000}
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>City</Label>
                      <TextInput
                        value={state.city}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            city: value,
                          }))
                        }
                        placeholder="City"
                        maxLength={1000}
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>State</Label>
                      <TextInput
                        value={state.state}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            state: value,
                          }))
                        }
                        placeholder="State"
                        maxLength={1000}
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                </Grid>
                <Grid>
                  <GridItem>
                    <Fieldset>
                      <Label required>Pincode</Label>
                      <TextInput
                        value={state.pincode}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            pincode: value,
                          }))
                        }
                        placeholder="Pincode"
                        maxLength={8}
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>Turnover</Label>
                      <TextInput
                        value={state.turnover}
                        isNumeric
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            turnover: value,
                          }))
                        }
                        placeholder="Turnover"
                        maxLength={1000}
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem></GridItem>
                  <GridItem></GridItem>
                </Grid>
              </Fieldset>

              <Fieldset forlegend display="block">
                <Legend>KYC Details</Legend>
                <Grid>
                  <GridItem>
                    <Fieldset>
                      <Label required>PAN Number</Label>
                      <TextInput
                        value={state.panNumber}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            panNumber: value,
                          }))
                        }
                        placeholder="PAN Number"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>PAN Name</Label>
                      <TextInput
                        value={state.panName}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            panName: value,
                          }))
                        }
                        placeholder="PAN Name"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>Aadhaar Number</Label>
                      <TextInput
                        value={state.aadhaarNumber}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            aadhaarNumber: value,
                          }))
                        }
                        placeholder="Aadhaar Number"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required>Aadhaar Name</Label>
                      <TextInput
                        value={state.aadhaarName}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            aadhaarName: value,
                          }))
                        }
                        placeholder="Aadhaar Name"
                        disabled={
                          state.isLoading || state.kycStatus === "Approved"
                        }
                      />
                    </Fieldset>
                  </GridItem>
                </Grid>
              </Fieldset>

              {metaData?.autoWalletTransferStatus === "Active" && (
                <Fieldset forlegend display="block">
                  <Legend>Wallet Controls</Legend>
                  <Grid>
                    <GridItem>
                      <Fieldset>
                        <Label required>Auto Wallet Transfer Status</Label>
                        {state.autoWalletTransferStatus && (
                          <Switch
                            value={state.autoWalletTransferStatus === "Yes"}
                            onChange={(value) => {
                              setState((prevState) => ({
                                ...prevState,
                                autoWalletTransferStatus: value ? "Yes" : "No",
                              }));
                            }}
                          />
                        )}
                      </Fieldset>
                    </GridItem>
                    <GridItem>
                      <Fieldset>
                        <Label required>Auto Wallet Transfer Amount</Label>
                        <TextInput
                          value={state.autoWalletTransferAmount}
                          isNumeric={true}
                          onChange={(value) =>
                            setState((prevState) => ({
                              ...prevState,
                              autoWalletTransferAmount: value,
                            }))
                          }
                          placeholder="Auto Wallet Transfer Amount"
                          disabled={state.isLoading}
                        />
                      </Fieldset>
                    </GridItem>
                    <GridItem></GridItem>
                    <GridItem></GridItem>
                  </Grid>
                </Fieldset>
              )}

              <Fieldset forlegend display="block">
                <Legend>Alert Control</Legend>
                <Grid>
                  <GridItem>
                    <Fieldset>
                      <Label>Alert Number</Label>
                      <MobileInput
                        value={state.alertNumber}
                        isNumeric={true}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            alertNumber: value,
                          }))
                        }
                        placeholder="Alert Number"
                        disabled={state.isLoading}
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label>Email</Label>
                      <TextInput
                        value={state.email}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            email: value,
                          }))
                        }
                        placeholder="Email Address"
                        disabled={state.isLoading}
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label>Alert Type</Label>
                      <Dropdown
                        value={state.alertType}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            alertType: value,
                          }))
                        }
                        placeholder="Alert Type"
                        disabled={state.isLoading}
                        options={[
                          {
                            name: "-- SELECT ALERT TYPE --",
                            value: "",
                          },
                          {
                            name: "Whatsapp",
                            value: "Whatsapp",
                          },
                          {
                            name: "Email",
                            value: "Email",
                          },
                        ]}
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem></GridItem>
                </Grid>
                <Grid>
                  <GridItem>
                    <Fieldset>
                      <Label>
                        Low Balance Alert
                      </Label>
                      {state.lowBalAlertStatus && (
                        <Switch
                          value={state.lowBalAlertStatus === "Yes"}
                          onChange={(value) => {
                            setState((prevState) => ({
                              ...prevState,
                              lowBalAlertStatus: value ? "Yes" : "No",
                            }));
                          }}
                        />
                      )}
                    </Fieldset>
                  </GridItem>
                  <GridItem>
                    <Fieldset>
                      <Label required={state.lowBalAlertStatus === "Yes"}>Low Balance Alert Amount</Label>
                      <TextInput
                        value={state.lowBalAlertAmount}
                        isNumeric={true}
                        onChange={(value) =>
                          setState((prevState) => ({
                            ...prevState,
                            lowBalAlertAmount: value,
                          }))
                        }
                        placeholder="Low Balance Alert Amount"
                        disabled={state.isLoading}
                      />
                    </Fieldset>
                  </GridItem>
                  <GridItem></GridItem>
                  <GridItem></GridItem>
                </Grid>
              </Fieldset>

              {state.responseStatus && state.responseMessage && (
                <Fieldset>
                  <MessageBox
                    status={state.responseStatus}
                    message={state.responseMessage}
                  />
                </Fieldset>
              )}
              <Fieldset>
                <Button disabled={state.isLoading}>Save</Button>
              </Fieldset>
            </Form>
          </StyledFormContainer>
        </StyledMainAreaContainer>
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(Profile);
